import type { ComponentType, ReactNode } from 'react';

/**
 * Shared engine contract. See docs/calculator-engine-architecture.md §4.
 * This file is the one place a change ripples out to every calculator —
 * keep it deliberately small.
 */

// `(string | number)[]` is the checkboxGroup value shape (HW Index Phase 5,
// the engine's first multi-select field) — a plain array so the reducer's
// existing setValue/dispatch (generic over TValues[keyof TValues]) needs no
// changes at all.
export type FieldValues = Record<string, string | number | boolean | (string | number)[] | null>;

export interface Option {
  label: string;
  value: string | number;
  hint?: string;
  /** Per-option "?" tooltip (RadioGroup only) — HW Index's plan-type question
   * has a distinct explanation for each of its 8 options, unlike every other
   * calculator's tooltips, which are one per field (FieldRow's `tooltip`). */
  tooltip?: string;
}

/** Lets a max/min come from IRS data without a calculator reaching into limits itself. */
export type NumberOrLimit = number | ((ctx: ValidationContext) => number);

export interface ValidationContext {
  limits: IrsLimitsSet;
  values: FieldValues;
}

export interface LabelContext {
  limits: IrsLimitsSet;
}

// ---------------------------------------------------------------------------
// Fields
// ---------------------------------------------------------------------------

interface BaseField<TValues extends FieldValues> {
  kind: 'field';
  name: keyof TValues & string;
  /** Function form supports year/branch-dependent question wording. */
  label: string | ((v: TValues, ctx: LabelContext) => string);
  /** When the visible label is not the accessible name. */
  srLabel?: string;
  /** Replaces the legacy .calc-tooltip anchors. */
  tooltip?: string;
  help?: ReactNode;
  /** Default true. */
  required?: boolean;
  visibleIf?: (v: TValues, ctx: ValidationContext) => boolean;
  /** Field-level validation. Return null when valid. */
  validate?: (value: unknown, v: TValues, ctx: ValidationContext) => string | null;
  /** Clears the field's value when this becomes true (e.g. a branch flip). */
  resetWhen?: (v: TValues) => boolean;
}

export type FieldVariant =
  | { type: 'currency'; min?: NumberOrLimit; max?: NumberOrLimit; step?: number; stepper?: boolean }
  | {
      type: 'number';
      min?: NumberOrLimit;
      max?: NumberOrLimit;
      step?: number;
      stepper?: boolean;
      /** Stepper snaps to these values instead of incrementing (e.g. HSA Savings Spotlight's years). */
      snapTo?: number[];
    }
  | {
      type: 'percent';
      min?: NumberOrLimit;
      max?: NumberOrLimit;
      step?: number;
      stepper?: boolean;
      /** Federal-bracket walk: stepper snaps to these values instead of incrementing. */
      snapTo?: number[];
    }
  | { type: 'text' }
  | { type: 'radioGroup'; options: Option[]; layout?: 'inline' | 'stacked' | 'scale' }
  | {
      type: 'checkboxGroup';
      options: Option[];
      /** e.g. "None of the above" — clears all others when picked, and vice versa. */
      exclusiveValue?: string | number;
    }
  | { type: 'select'; options: Option[] | ((v: FieldValues, ctx: ValidationContext) => Option[]) }
  | { type: 'slider'; min: number; max: NumberOrLimit; step?: number; readOnlyWhen?: (v: FieldValues) => boolean }
  | { type: 'custom'; render: (p: FieldRenderProps) => ReactNode };

export type FieldConfig<TValues extends FieldValues> = BaseField<TValues> & FieldVariant;

export interface FieldRenderProps {
  value: unknown;
  setValue: (value: unknown) => void;
  blur: () => void;
  error: string | null;
  touched: boolean;
  id: string;
}

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

/** A step item is an input field, a piece of presentation, or an escape hatch. */
export type StepItem<TValues extends FieldValues> =
  | FieldConfig<TValues>
  | { kind: 'heading'; text: string | ((v: TValues) => string); visibleIf?: (v: TValues) => boolean }
  | { kind: 'note'; content: ReactNode | ((v: TValues) => ReactNode); visibleIf?: (v: TValues) => boolean }
  | { kind: 'custom'; id: string; render: (p: StepItemRenderProps<TValues>) => ReactNode };

export interface StepItemRenderProps<TValues extends FieldValues> {
  values: TValues;
  setValue: <K extends keyof TValues>(name: K, value: TValues[K]) => void;
}

export interface StepLayoutProps<TValues extends FieldValues> {
  values: TValues;
  setValue: <K extends keyof TValues>(name: K, value: TValues[K]) => void;
  errors: Partial<Record<keyof TValues, string>>;
}

export interface StepConfig<TValues extends FieldValues> {
  /** Stable slug, kept aligned with legacy hash-route names where one existed. */
  id: string;
  /** Progress-group label. Steps sharing a group render as one progress tick. */
  group?: string;
  heading?: string | ((v: TValues) => string);
  intro?: ReactNode | ((v: TValues) => ReactNode);

  items: StepItem<TValues>[];

  /** Whole-step / cross-field validation. Returns per-field messages. */
  validate?: (v: TValues, ctx: ValidationContext) => Partial<Record<keyof TValues, string>> | null;

  /** Reserved for a step that is skipped by a branch. Rarely used — see §4.3. */
  visibleIf?: (v: TValues) => boolean;

  /** Escape hatch for a step whose arrangement is not a vertical field list. */
  layout?: ComponentType<StepLayoutProps<TValues>>;

  /** e.g. "GET RESULTS" on the last step. Defaults to "NEXT". */
  nextLabel?: string;
}

export interface IntroConfig<TValues extends FieldValues = FieldValues> {
  heading: string;
  body?: ReactNode;
  ctaLabel: string;
  /** Same item shape as a step's — a field, heading/note, or the `custom` escape hatch (e.g. a plan-pair choice with dynamic option labels). */
  fields?: StepItem<TValues>[];
  media?: ReactNode;
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export interface Formatters {
  currency: (n: number) => string;
  percent: (n: number) => string;
  number: (n: number) => string;
}

export interface ResultsViewProps<TValues extends FieldValues, TResult> {
  result: TResult;
  values: TValues;
  limits: IrsLimitsSet;
  /** Live edits from within results (e.g. a post-results contribution stepper). */
  setValue: <K extends keyof TValues>(name: K, value: TValues[K]) => void;
  /** Jump back to a named step (Plan Comparison's EDIT link). */
  goToStep: (stepId: string) => void;
  /** Return to the intro and clear values. */
  reset: () => void;
  fmt: Formatters;
}

// ---------------------------------------------------------------------------
// Top-level calculator config
// ---------------------------------------------------------------------------

export interface StepChangeEvent {
  calculatorId: string;
  stepId: string;
  stepIndex: number;
  stepCount: number;
}

export interface CalculatorConfig<TValues extends FieldValues, TResult> {
  /** Stable id; used for the mount div, analytics, and the bundle name. */
  id: string;
  /** Human name, used in the progress header and analytics payloads. */
  title: string;
  /** Optional landing screen shown before step 0. Omit for a calculator with no intro. */
  intro?: IntroConfig<TValues>;
  /** Initial values. Every field named in `steps` must have a key here. */
  initialValues: TValues;
  /** Ordered screens. May be a single entry. */
  steps: StepConfig<TValues>[];
  /** Pure. No DOM, no React, no side effects. The unit-test surface. */
  calculate: (values: TValues, limits: IrsLimitsSet) => TResult;
  /** Rendered when results are visible (see resultsPlacement). */
  ResultsView: ComponentType<ResultsViewProps<TValues, TResult>>;
  /**
   * 'terminal' (default) — results replace the step panel after the last step.
   * 'always'            — results render beside the step panel at all times
   *                        (HSA Savings).
   */
  resultsPlacement?: 'terminal' | 'always';
  /**
   * 'onComplete' (default) — calculate() runs when results first become visible.
   * 'live'                 — calculate() runs on every change to a valid value.
   * In BOTH modes, once results are visible, any input change recomputes.
   * See §4.5 of the architecture doc.
   */
  recompute?: 'onComplete' | 'live';
  /** Progress indicator. 'none' for HSA Savings. */
  progress?: 'groups' | 'steps' | 'none';
  /** Optional analytics hook; the engine supplies a dataLayer default. */
  onStepChange?: (evt: StepChangeEvent) => void;
  /**
   * Layout variant name, emitted as data-layout on the .hsab-calc root so a
   * calculator's stylesheet can key an alternate layout off it. Omit for the
   * default layout ('legacy').
   */
  layout?: string;
  /**
   * With resultsPlacement 'always': 'after' (default) renders the step panel
   * first, then results; 'before' puts results first in DOM (and so tab/reading)
   * order.
   */
  resultsOrder?: 'after' | 'before';
  /**
   * 'flank' (default): decrease button left of the input, increase right.
   * 'arrows': circled up/down arrow buttons trailing the input.
   * 'stacked': up/down chevrons stacked at the input's right edge.
   */
  stepperLayout?: 'flank' | 'arrows' | 'stacked';
  /**
   * Accessible names for every stepper button in this calculator. Omit for
   * 'Increase' / 'Decrease'. HSA Savings passes prod's legacy wording.
   */
  stepperLabels?: { increase: string; decrease: string };
  /**
   * 'box' (default): the value sits in a bordered text box.
   * 'inline': no box; the value reads as bold text with thousands separators
   * (while not being edited) and the steppers beside it (HSA Savings Spotlight).
   * 'fit': still a box, but the input is only as wide as its value (no
   * thousands separators), so a "$" prefix sits against the number instead of
   * at the far edge of a right-aligned field (HSA Savings Odonlow).
   * Its look is the layout's own stylesheet.
   */
  inputStyle?: 'box' | 'inline' | 'fit';
  /**
   * What happens when a typed value is out of range (failing its validate()).
   * 'clamp' (default): on blur the value is silently clamped to min/max, so
   * the error message is never actually seen.
   * 'hold': the typed value stays in the field and its error message shows
   * (on blur), and results keep using that field's last VALID value until
   * it's corrected -- the legacy jQuery HSA Savings behavior on prod
   * (content team, 2026-10-08). Steppers still clamp in both modes.
   */
  invalidInput?: 'clamp' | 'hold';
}

/**
 * What an entry file hands autoMount(): one config, or a resolver that picks a
 * config per mount from that placement's bootstrap options (e.g. HSA Savings'
 * per-placement Legacy/Spotlight layout).
 */
export type ConfigSource<TValues extends FieldValues, TResult> =
  | CalculatorConfig<TValues, TResult>
  | ((options: CalculatorBootstrap['options']) => CalculatorConfig<TValues, TResult>);

// ---------------------------------------------------------------------------
// IRS limits data contract — see §6
// ---------------------------------------------------------------------------

export interface IrsLimits {
  year: string;
  hsa: {
    maxContributionSingle: number;
    minDeductibleSingle: number;
    maxOutOfPocketSingle: number;
    catchupSingle: number;
    maxContributionFamily: number;
    minDeductibleFamily: number;
    maxOutOfPocketFamily: number;
    catchupFamily: number;
  };
  fsa: { maxContribution: number; maxCarryover: number };
  dcfsa: { maxContribution: number; filingJointlyMax: number };
  commuter: { maxContributionPerMonth: number };
  tuition: { maxContribution: number };
  adoption: { maxContribution: number };
}

export interface IrsLimitsSet {
  /** The limits node the author linked on the calculator entry. */
  current: IrsLimits;
  /** Every irs-limit node in the workspace, keyed by year string. */
  byYear: Record<string, IrsLimits>;
}

// ---------------------------------------------------------------------------
// Control style — see docs/calculator-control-style.md. A second, purely
// visual configuration axis orthogonal to color theming: Shape and Size of
// buttons/inputs/radio-checkbox controls, not color.
// ---------------------------------------------------------------------------

/** Adding a fourth shape: extend this union, add a YAML option (card-level
 *  and the per-component override), add one CSS block under
 *  `.hsab-calc[data-shape='…']`. No component changes. */
export type ControlShape = 'default' | 'square' | 'round';
export type ControlSize = 'large' | 'small';

/**
 * Screen/section transition style (Brad, 2026-09-28): wizard step changes
 * (intro -> steps -> results) and HSA Savings Spotlight's plan-type gate.
 * Carried with Control Style since it's the same kind of per-card,
 * per-placement-overridable visual setting.
 */
export type ControlAnimation = 'none' | 'fade' | 'slide';

export interface ControlStyle {
  shape: ControlShape;
  size: ControlSize;
  animation: ControlAnimation;
  /** Result numbers roll to new values, odometer-style (engine/chrome/Odometer.tsx). Off unless set. */
  countUp: boolean;
}

/** Today's rendering. Used whenever the blob omits or mis-spells controlStyle. */
export const DEFAULT_CONTROL_STYLE: ControlStyle = {
  shape: 'default',
  size: 'large',
  animation: 'fade',
  countUp: false,
};

export interface CalculatorBootstrap {
  calculatorId: string;
  limits: IrsLimitsSet;
  /**
   * Card-level default, with an optional per-component-instance override
   * already resolved by calculator-app.ftl (override wins when set) — the
   * bootstrap only ever carries the one final value, same pattern as
   * gridlineStyle below. Optional: a page cached before this feature
   * shipped emits no controlStyle. Absent resolves to
   * DEFAULT_CONTROL_STYLE — today's look.
   */
  controlStyle?: ControlStyle;
  options: {
    contextPath: string;
    assetBase: string;
    /** Replaces the legacy window.location.pathname sniff for the img-states swap. */
    isStateSite: boolean;
    /** cmsfn.editMode — enables verbose in-place authoring error panels. */
    editMode: boolean;
    /**
     * HSA Savings only — author-set on this specific component instance's
     * own dialog (dialogs/components/calculators/hsaSavingsCalc.yaml), a
     * per-placement override, not a global default on the calculator card
     * (an earlier version lived there — see calculator-app.ftl's commented-
     * out reference). Empty string / undefined = no gridlines. Only
     * `'dotted'` exists today; more values (solid, bold, ...) are meant to
     * be added here later, each needing a matching
     * .hsab-calc-savings__gridline--<value> CSS rule and nothing else.
     */
    gridlineStyle?: string;
    /**
     * HSA Savings only — per-placement layout from hsaSavingsCalc.yaml's
     * calcLayout. '' / undefined / 'legacy' = legacy layout; 'spotlight' =
     * the redesign. Absent on pages cached before this shipped.
     */
    layout?: string;
    /**
     * HSA Savings only -- optional modules switched on for this placement
     * (hsaSavingsCalc.yaml's Modules checkboxes), e.g. ['balance-mix'].
     * Absent / empty = none. Unknown names are ignored.
     */
    modules?: string[];
  };
}
